import React, { ReactNode, useEffect, useRef, useState } from "react";
import "./styles/ScreenTransitioner.sass";

// how long the outgoing screen takes to fade out (matches .LeavingScreen in the sass).
// Once it's invisible we unmount it; the incoming screen then fades in from the backdrop.
const FADE_OUT_MS = 600;

const ScreenTransitioner = ({ children }: { children: ReactNode[] }) => {
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  // the outgoing screen, kept mounted only while it fades out, then dropped
  const [prevScreenIndex, setPrevScreenIndex] = useState<number | null>(null);
  const screenTimeout = useRef<NodeJS.Timeout | null>(null);
  const fadeTimeout = useRef<NodeJS.Timeout | null>(null);

  // advance to the next screen after the current one's duration
  useEffect(() => {
    const currentChild = children[currentScreenIndex];
    if (!currentChild) return;
    // @ts-ignore
    const duration = currentChild.props.duration;

    screenTimeout.current = setTimeout(() => {
      setPrevScreenIndex(currentScreenIndex);
      setCurrentScreenIndex((i) => (i + 1) % children.length);
    }, duration);

    return () => {
      if (screenTimeout.current) clearTimeout(screenTimeout.current);
    };
  }, [currentScreenIndex, children]);

  // once the outgoing screen has faded out, unmount it so only 1 heavy layer stays
  // composited (2 only briefly during the fade-out) — keeps the TV happy
  useEffect(() => {
    if (prevScreenIndex === null) return;
    fadeTimeout.current = setTimeout(() => setPrevScreenIndex(null), FADE_OUT_MS);
    return () => {
      if (fadeTimeout.current) clearTimeout(fadeTimeout.current);
    };
  }, [prevScreenIndex]);

  const renderScreen = (index: number, active: boolean) => {
    const child = children[index];
    if (!child) return null;
    // @ts-ignore
    const cloned = React.cloneElement(child, { isActive: active });
    return (
      <li
        className={active ? "Screen ActiveScreen" : "Screen LeavingScreen"}
        key={`screen-${index}`}
      >
        {cloned}
      </li>
    );
  };

  return (
    <div className="ScreenTransitioner">
      {prevScreenIndex !== null &&
        prevScreenIndex !== currentScreenIndex &&
        renderScreen(prevScreenIndex, false)}
      {renderScreen(currentScreenIndex, true)}
    </div>
  );
};

export default ScreenTransitioner;
